import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaStar, FaCog } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { apiHelper } from '../utils/apiHelper';
import { normalizeProfileImageUrl } from '../utils/imageHelper';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  padding: 0 5%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(10, 2, 28, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 999;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    justify-content: center;
    height: auto;
    padding: 10px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  margin: 0 auto;
`;

const SearchBox = styled.form`
  display: flex;
  align-items: center;
  background: rgba(20, 10, 30, 0.8);
  padding: 8px 15px;
  border-radius: 3px;
  border: 1px solid var(--border-color);
  width: 500px;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: var(--sidebar-indicator-color);
    width: 600px;
  }

  input {
    background: transparent;
    border: none;
    outline: none;
    color: var(--primary-color);
    font-size: 1rem;
    margin-left: 8px;
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 350px;
    padding: 6px 12px;
    
    &:focus-within {
      width: 400px;
    }
  }

  @media (max-width: 480px) {
    width: 280px;
    
    &:focus-within {
      width: 320px;
    }
  }
`;

const SearchResults = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  transform: none;
  width: 100%;
  max-width: 100%;
  background: #1c102c;
  border: 1px solid var(--border-color);
  border-radius: 3px;
  padding: 15px;
  max-height: 500px;
  overflow-y: auto;
  box-shadow: 0 4px 15px rgba(0,0,0,0.4);
  z-index: 1001;
  margin-top: 5px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ResultItem = styled(Link)`
  display: flex;
  gap: 15px;
  padding: 15px;
  border-radius: 3px;
  color: var(--secondary-color);
  text-decoration: none;
  transition: 0.2s;
  font-size: 0.95rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 10px;
  position: relative;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  &:hover, &.active {
    background-color: rgba(255, 255, 255, 0.15);
    color: var(--primary-color);
  }

  &.active::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 70%;
    background: var(--sidebar-indicator-color);
    border-radius: 2px;
  }

  .poster {
    width: 80px;
    height: 120px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
  }

  .title-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--primary-color);
    line-height: 1.2;
  }

  .meta-info {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 0.75rem;
    color: var(--secondary-color);
  }

  .rating {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #ffc107;
    font-weight: 600;
  }

  .year {
    color: var(--gradient-pink);
    font-weight: 500;
  }

  .media-type {
    font-size: 0.7rem;
    padding: 3px 8px;
    border-radius: 12px;
    background: var(--gradient-cyan);
    color: #111;
    font-weight: 600;
    white-space: nowrap;
    align-self: flex-start;
  }

  .overview {
    font-size: 0.8rem;
    line-height: 1.4;
    color: var(--secondary-color);
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid var(--border-color);
  transition: all 0.3s ease;
  margin-top: 2px;

  &:hover {
    border-color: var(--gradient-pink);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const LoginButton = styled.button`
  padding: 8px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--button-bg-start), var(--button-bg-end));
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
  }

  @media (max-width: 768px) {
    padding: 6px 16px;
    font-size: 0.9rem;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 52px;
  right: 0;
  width: 180px;
  background: rgba(15, 5, 25, 0.98);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px;
  z-index: 1002;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    width: 160px;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 10px 12px;
  border-radius: 6px;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.9rem;
  transition: 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--primary-color);
  text-align: left;
  font-size: 0.9rem;
  cursor: pointer;
  transition: 0.2s;
  
  &:hover {
    background-color: rgba(255, 0, 127, 0.1);
    color: var(--gradient-pink);
  }
`;

const calculateSimilarity = (str1, str2) => {
  const cleanStr1 = str1.toLowerCase().replace(/[^a-z0-9ğüşıöç\s]/g, '');
  const cleanStr2 = str2.toLowerCase().replace(/[^a-z0-9ğüşıöç\s]/g, '');
  
  // Tam eşleşme
  if (cleanStr1 === cleanStr2) return 1;
  
  // KELİME BAŞLANGIÇLARINI KONTROL ET
  const words1 = cleanStr1.split(/\s+/);
  const words2 = cleanStr2.split(/\s+/);
  
  let allWordsMatch = true;
  
  // Tüm kelimelerin başlangıçları eşleşiyor mu?
  for (let i = 0; i < Math.min(words1.length, words2.length); i++) {
    if (!words2[i].startsWith(words1[i])) {
      allWordsMatch = false;
      break;
    }
  }
  
  if (allWordsMatch && words1.length > 0) {
    return 0.9;
  }
  
  // HER KELİME İÇİN BAŞLANGIÇ KONTROLÜ
  let startMatchScore = 0;
  words1.forEach(word1 => {
    if (word1.length < 2) return;
    words2.forEach(word2 => {
      if (word2.startsWith(word1)) {
        startMatchScore += 0.7;
      }
    });
  });
  
  if (startMatchScore > 0) {
    return Math.min(0.85, startMatchScore / words1.length);
  }
  
  // İçerme kontrolü
  if (cleanStr2.includes(cleanStr1) && cleanStr1.length >= 2) {
    return 0.8 + (cleanStr1.length / cleanStr2.length) * 0.2;
  }
  
  // Başlangıç kontrolü
  if (cleanStr2.startsWith(cleanStr1) && cleanStr1.length >= 2) {
    return 0.9;
  }
  
  // Kelime bazlı eşleşme
  let wordMatchScore = 0;
  words1.forEach(word1 => {
    if (word1.length < 2) return;
    words2.forEach(word2 => {
      if (word2.startsWith(word1)) {
        wordMatchScore += 0.6;
      } else if (word2.includes(word1)) {
        wordMatchScore += 0.4;
      }
    });
  });
  
  if (wordMatchScore > 0) {
    return Math.min(0.8, wordMatchScore / words1.length);
  }
  
  // Levenshtein distance (son çare)
  const track = Array(cleanStr2.length + 1).fill(null).map(() =>
    Array(cleanStr1.length + 1).fill(null));
  
  for (let i = 0; i <= cleanStr1.length; i += 1) {
    track[0][i] = i;
  }
  
  for (let j = 0; j <= cleanStr2.length; j += 1) {
    track[j][0] = j;
  }
  
  for (let j = 1; j <= cleanStr2.length; j += 1) {
    for (let i = 1; i <= cleanStr1.length; i += 1) {
      const indicator = cleanStr1[i - 1] === cleanStr2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1,
        track[j - 1][i] + 1,
        track[j - 1][i - 1] + indicator,
      );
    }
  }
  
  const maxLength = Math.max(cleanStr1.length, cleanStr2.length);
  return maxLength === 0 ? 1 : (maxLength - track[cleanStr2.length][cleanStr1.length]) / maxLength;
};

const Header = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [siteContent, setSiteContent] = useState([]);
  const [alternateTitles, setAlternateTitles] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchWrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const resultsRef = useRef([]);

  // Kullanıcı oturumunu kontrol et
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_AUTH), {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.success) {
          setUser(data.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      }
    };

    checkAuth();
  }, []);

  // MySQL'den ANA İÇERİKLERİ çek
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
        if (response.ok) {
          const data = await response.json();
          const availableContent = data.filter(item => 
            item.backdrop_path || item.poster_path
          );
          setSiteContent(availableContent);
        }
      } catch (error) {
        console.error("İçerik çekme hatası:", error);
      }
    };

    fetchContent();
  }, []);

  // Alternate titles'ları çek
  useEffect(() => {
    const fetchAlternateTitles = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.GET_ALL_ALTERNATE_TITLES), {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && Array.isArray(data.data)) {
            const titlesMap = {};
            data.data.forEach(title => {
              if (title.content_id && title.title && title.title.trim() !== '') {
                if (!titlesMap[title.content_id]) {
                  titlesMap[title.content_id] = [];
                }
                titlesMap[title.content_id].push(title.title);
              }
            });
            
            setAlternateTitles(titlesMap);
            return;
          }
        }
        
        // Fallback: Eski sisteme dön
        fallbackToOldSystem();
        
      } catch (error) {
        fallbackToOldSystem();
      }
    };

    // Fallback fonksiyonu
    const fallbackToOldSystem = () => {
      const titlesMap = {};
      
      siteContent.forEach(item => {
        if (item.alternate_titles) {
          try {
            const altTitles = typeof item.alternate_titles === 'string' 
              ? JSON.parse(item.alternate_titles || '[]') 
              : item.alternate_titles;
            
            if (Array.isArray(altTitles) && altTitles.length > 0) {
              titlesMap[item.id] = altTitles.filter(title => 
                title && typeof title === 'string' && title.trim() !== ''
              );
            }
          } catch (e) {
            // Parse hatasını görmezden gel
          }
        }
      });
      
      setAlternateTitles(titlesMap);
    };

    if (siteContent.length > 0) {
      fetchAlternateTitles();
    }
  }, [siteContent]);

  // Giriş yap
  const handleLogin = () => {
    navigate('/login');
  };

  // Çıkış yap
  const handleLogout = async () => {
    try {
      const response = await apiHelper.logout();
      const data = await response.json();
      
      if (data.success) {
        setUser(null);
        setMenuOpen(false);
        navigate('/');
      }
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
  };

  // AKILLI ARAMA - ALTERNATE TITLES DESTEKLİ
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      const query = searchQuery.trim().toLowerCase();
      
      const resultsWithScores = siteContent.map((item) => {
        let maxScore = 0;
        
        // 1. ANA BAŞLIK kontrolü
        if (item.title) {
          const titleScore = calculateSimilarity(query, item.title);
          maxScore = Math.max(maxScore, titleScore);
        }
        
        // 2. ORJİNAL BAŞLIK kontrolü
        if (item.original_title && item.original_title !== item.title) {
          const originalScore = calculateSimilarity(query, item.original_title);
          maxScore = Math.max(maxScore, originalScore);
        }
        
        // 3. ALTERNATE TITLES kontrolü
        const itemAlternateTitles = alternateTitles[item.id] || [];
        itemAlternateTitles.forEach(altTitle => {
          const altScore = calculateSimilarity(query, altTitle);
          maxScore = Math.max(maxScore, altScore);
        });
        
        // 4. Alternatif başlıklar (eski sistem - yedek)
        if (item.alternate_titles) {
          const altTitles = Array.isArray(item.alternate_titles) 
            ? item.alternate_titles 
            : JSON.parse(item.alternate_titles || '[]');
          
          altTitles.forEach(altTitle => {
            const altScore = calculateSimilarity(query, altTitle);
            maxScore = Math.max(maxScore, altScore);
          });
        }
        
        return { item, score: maxScore };
      });
      
      // SONUÇLARI FİLTRELE VE SIRALA
      const filtered = resultsWithScores
        .filter(({ score }) => score > 0.15)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return (b.item.vote_count || 0) - (a.item.vote_count || 0);
        })
        .map(({ item }) => item);
      
      setSearchResults(filtered);
      setSelectedIndex(filtered.length > 0 ? 0 : -1);
    } else {
      setSearchResults([]);
      setSelectedIndex(-1);
    }
  }, [searchQuery, siteContent, alternateTitles]);

  // Klavye navigasyonu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (searchResults.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        const selectedItem = searchResults[selectedIndex];
        if (selectedItem) {
          navigate(getMediaRoute(selectedItem));
          setSearchQuery("");
          setSearchResults([]);
          setSelectedIndex(0);
        }
      } else if (e.key === 'Escape') {
        setSearchResults([]);
        setSelectedIndex(0);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchResults, selectedIndex, navigate]);

  // Seçili öğeyi scroll et
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current[selectedIndex]) {
      resultsRef.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // Dış tıklama kapatma
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setSearchResults([]);
        setSelectedIndex(0);
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Media type'a göre etiket
  const getMediaTypeLabel = (mediaType) => {
    switch (mediaType) {
      case 'anime': return 'ANIME';
      case 'tv': return 'DIZI';
      case 'movie': return 'FILM';
      default: return mediaType?.toUpperCase();
    }
  };

  // Media type'a göre route
  const getMediaRoute = (item) => {
    if (item.media_type === 'anime') {
      return `/anime/${item.tmdb_id || item.id}`;
    } else {
      return `/${item.media_type}/${item.tmdb_id || item.id}`;
    }
  };

  // Yıl bilgisini çıkar
  const getYear = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    const year = new Date(dateString).getFullYear();
    return isNaN(year) ? 'Bilinmiyor' : year;
  };

  // Proxy image URL oluştur
const getPosterUrl = (posterPath) => {
  if (!posterPath) return '/placeholder-image.jpg';

  if (posterPath.includes('/proxy-image.php')) return posterPath;

  return getImageUrl(posterPath, 'w154'); // küçük boy poster
};

    const getImageUrl = (path, size = 'w500') => {
    if (!path) return '/placeholder-image.jpg';
    
    if (path.includes('/proxy-image.php')) return path;

    // Tek bir proxy yolu kullanıyoruz
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(path)}&size=${size}`;
  };

  return (
    <HeaderContainer
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <SearchWrapper ref={searchWrapperRef}>
        <SearchBox onSubmit={(e) => e.preventDefault()}>
          <FaSearch color="var(--secondary-color)" size={16} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Film, dizi veya anime ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
        </SearchBox>

        <AnimatePresence>
          {searchResults.length > 0 && (
            <SearchResults
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {searchResults.slice(0, 6).map((item, index) => (
                <ResultItem
                  key={item.id}
                  to={getMediaRoute(item)}
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                    setSelectedIndex(0);
                  }}
                  className={selectedIndex === index ? 'active' : ''}
                  ref={el => resultsRef.current[index] = el}
                >
                  <img 
                    className="poster"
                    src={getPosterUrl(item.poster_path)}
                    alt={item.title}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iMTIwIiB2aWV3Qm94PSIwIDAgODAgMTIwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSI4MCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiMxMTEiLz48dGV4dCB4PSI0MCIgeT0iNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                    }}
                  />
                  <div className="content">
                    <div className="header-row">
                      <div className="title-section">
                        <div className="title">{item.title}</div>
                        <div className="meta-info">
                          {item.vote_average > 0 && (
                            <div className="rating">
                              <FaStar size={12} />
                              {parseFloat(item.vote_average).toFixed(1)}
                            </div>
                          )}
                          <div className="year">
                            {getYear(item.release_date)}
                          </div>
                        </div>
                      </div>
                      <div className="media-type">
                        {getMediaTypeLabel(item.media_type)}
                      </div>
                    </div>
                    
                    {item.overview && (
                      <div className="overview">
                        {item.overview.length > 200 
                          ? `${item.overview.substring(0, 200)}...` 
                          : item.overview
                        }
                      </div>
                    )}
                  </div>
                </ResultItem>
              ))}
            </SearchResults>
          )}
        </AnimatePresence>
      </SearchWrapper>

      <UserSection ref={dropdownRef}>
        {user ? (
          <>
            <UserAvatar 
              src={normalizeProfileImageUrl(user.avatar_url) || '/placeholder-image.jpg'} 
              alt={user.username} 
              onClick={() => setMenuOpen(!menuOpen)}
              onError={(e) => {
                e.target.src = '/placeholder-image.jpg';
              }}
            />
            <AnimatePresence>
              {menuOpen && (
                <DropdownMenu
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                <DropdownItem to={`/profil/${user.username}`} onClick={() => setMenuOpen(false)}>
                  Profilim
                </DropdownItem>
                {(user.role === 'admin' || user.role === 'moderator') && (
                  <DropdownItem 
                    to="/admin" 
                    onClick={() => setMenuOpen(false)}
                  >
                    Admin Paneli
                  </DropdownItem>
                )}
                  <LogoutButton onClick={handleLogout}>
                    Çıkış Yap
                  </LogoutButton>
                </DropdownMenu>
              )}
            </AnimatePresence>
          </>
        ) : (
          <LoginButton onClick={handleLogin}>
            Giriş Yap
          </LoginButton>
        )}
      </UserSection>
    </HeaderContainer>
  );
};

export default Header;
